import React, { useState } from "react";
import { Mail, Search, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";
import { timeAgo } from "./InboxPage.helpers";

/* ── Status dot color by index ────────────────────────────────────────────── */
const statusColors = ["#fcd83f", "#60a5fa", "#9ca3af"]; // amber, blue, gray

function ThreadSkeleton() {
  return (
    <div className="px-2 space-y-1">
      {[...Array(5)].map((_, i) => (
        <div key={`sk-${i}`} className="flex gap-4 p-4 rounded-2xl animate-pulse">
          <div className="w-12 h-12 rounded-xl flex-shrink-0" style={{ background: "rgba(255,255,255,0.06)" }} />
          <div className="flex-1 space-y-2.5">
            <div className="flex justify-between">
              <div className="h-3 rounded w-2/5" style={{ background: "rgba(255,255,255,0.06)" }} />
              <div className="h-2.5 rounded w-1/6" style={{ background: "rgba(255,255,255,0.06)" }} />
            </div>
            <div className="h-2.5 rounded w-3/4" style={{ background: "rgba(255,255,255,0.06)" }} />
            <div className="h-2 rounded w-5/6" style={{ background: "rgba(255,255,255,0.06)" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ filter }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-8 py-20 text-center">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(255,255,255,0.04)" }}>
        <Mail size={22} style={{ color: "rgba(255,255,255,0.2)" }} />
      </div>
      <p className="text-sm font-bold" style={{ color: "rgba(255,255,255,0.3)" }}>
        {filter === "unread" ? "Sin mensajes sin leer" : "Sin conversaciones"}
      </p>
      <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.2)" }}>Los emails aparecerán aquí</p>
    </div>
  );
}

export function ThreadList({ threads, selected, filter, loading, onSelect, search, onSearch, totalUnread, onThreadsChanged }) {
  const [checked, setChecked] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggleCheck = (id, e) => {
    e.stopPropagation();
    setChecked(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const clearSelection = () => setChecked(new Set());

  const deleteSelected = async () => {
    if (checked.size === 0) return;
    if (!window.confirm(`¿Eliminar ${checked.size} conversación(es)? Esta acción es irreversible.`)) return;
    setDeleting(true);
    try {
      const res = await authFetch(`${API}/inbox/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({ ids: Array.from(checked) }),
      });
      if (!res?.ok) throw new Error();
      const d = await res.json();
      toast.success(`${d.deleted || 0} conversación(es) eliminada(s)`);
      clearSelection();
      onThreadsChanged?.();
    } catch {
      toast.error("Error al eliminar");
    } finally { setDeleting(false); }
  };

  const deleteOne = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta conversación? Esta acción es irreversible.")) return;
    try {
      const res = await authFetch(`${API}/inbox/${id}`, { method: "DELETE" });
      if (!res?.ok) throw new Error();
      toast.success("Conversación eliminada");
      setChecked(prev => { const n = new Set(prev); n.delete(id); return n; });
      onThreadsChanged?.();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div
      className="flex flex-col w-full md:w-[360px] flex-shrink-0 overflow-hidden h-full"
      style={{ borderRight: "1px solid rgba(255,255,255,0.05)", background: "#0e0e0e" }}
    >
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 sm:py-6 flex-shrink-0">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2
            className="text-xl sm:text-2xl font-bold text-white"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Inbox
          </h2>
          {totalUnread > 0 && (
            <span
              className="text-xs font-bold px-2.5 py-1 rounded-full"
              style={{ background: "rgba(255,209,108,0.10)", color: "#ffd16c" }}
            >
              {totalUnread} Nuevos
            </span>
          )}
        </div>

        {/* Search */}
        <div className="relative group">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors"
            style={{ color: "#6b7280" }}
          />
          <input
            type="text"
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder="Buscar mensajes..."
            className="w-full rounded-xl py-3 pl-10 pr-4 text-sm text-white outline-none transition-all"
            style={{
              background: "#1a1a1a",
              border: "none",
              boxShadow: "none",
            }}
            onFocus={e => { e.currentTarget.style.ring = "1px"; e.currentTarget.style.outline = "1px solid rgba(255,209,108,0.4)"; }}
            onBlur={e => { e.currentTarget.style.outline = "none"; }}
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {checked.size > 0 && (
        <div
          data-testid="inbox-bulk-bar"
          className="flex items-center justify-between gap-2 px-4 py-2.5 mx-2 mb-2 rounded-xl"
          style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.15)" }}
        >
          <p className="text-xs font-bold text-white">{checked.size} seleccionada(s)</p>
          <div className="flex items-center gap-1">
            <button
              onClick={clearSelection}
              className="text-[11px] font-semibold text-gray-400 hover:text-white px-2 py-1 transition-colors"
            >
              Cancelar
            </button>
            <button
              data-testid="inbox-bulk-delete-btn"
              onClick={deleteSelected}
              disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors disabled:opacity-40"
              style={{ background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.25)" }}
            >
              {deleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
              Eliminar
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 space-y-1">
        {loading ? (
          <ThreadSkeleton />
        ) : threads.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          threads.map((thread, idx) => {
            const isActive = selected?.id === thread.id;
            const isChecked = checked.has(thread.id);
            const dotColor = thread.unread_count > 0 ? statusColors[0] : idx % 3 === 1 ? statusColors[1] : statusColors[2];
            const initials = (thread.contact_name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

            return (
              <div
                key={thread.id}
                data-testid={`thread-item-${thread.id}`}
                className="group w-full flex items-center transition-all rounded-2xl pr-1"
                style={isChecked
                  ? { background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }
                  : isActive
                    ? { background: "rgba(252,216,63,0.05)", border: "1px solid rgba(252,216,63,0.10)" }
                    : { background: "transparent", border: "1px solid transparent" }
                }
              >
                <button
                  data-testid={`thread-select-${thread.id}`}
                  onClick={(e) => toggleCheck(thread.id, e)}
                  aria-label={isChecked ? "Deseleccionar" : "Seleccionar"}
                  aria-pressed={isChecked}
                  className="flex-shrink-0 w-5 h-5 ml-3 rounded-md flex items-center justify-center transition-colors"
                  style={{
                    background: isChecked ? "#ef4444" : "transparent",
                    border: isChecked ? "1px solid #ef4444" : "1px solid rgba(255,255,255,0.15)",
                    opacity: isChecked ? 1 : 0.5,
                  }}
                >
                  {isChecked && (
                    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="4 10 8 14 16 6" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => onSelect(thread)}
                  className="flex-1 text-left p-3"
                  onMouseEnter={e => { if (!isActive && !isChecked) e.currentTarget.parentElement.style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={e => { if (!isActive && !isChecked) e.currentTarget.parentElement.style.background = "transparent"; }}
                >
                  <div className="flex gap-4">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                        style={{ background: "#262626", opacity: isActive ? 1 : 0.85 }}
                      >
                        {initials}
                      </div>
                      <span
                        className="absolute w-4 h-4 rounded-full border-4"
                        style={{ bottom: -4, right: -4, background: dotColor, borderColor: "#0e0e0e" }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        <h3 className={`font-bold truncate text-sm ${isActive ? "text-white" : "text-gray-200"}`}>
                          {thread.contact_name}
                        </h3>
                        <span
                          className="text-[10px] font-medium uppercase whitespace-nowrap ml-2"
                          style={{ color: thread.unread_count > 0 ? "#fcd83f" : "#6b7280" }}
                        >
                          {timeAgo(thread.last_message_at)}
                        </span>
                      </div>
                      <p className={`text-sm truncate ${thread.unread_count > 0 ? "text-white font-semibold" : "text-gray-400"}`}>
                        {thread.subject}
                      </p>
                      {thread.preview && (
                        <p className="text-xs mt-0.5 truncate" style={{ color: "var(--ds-text-muted)" }}>
                          {thread.preview}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
                <button
                  data-testid={`thread-delete-${thread.id}`}
                  onClick={(e) => deleteOne(thread.id, e)}
                  className="flex-shrink-0 p-2 mr-1 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                  style={{ color: "#9ca3af" }}
                  title="Eliminar conversación"
                  onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.background = "transparent"; }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
